import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { comma, pct, accuracyLabel, accuracyColor } from "../constants";
import Card from "./ui/Card";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Btn from "./ui/Btn";
import Modal from "./ui/Modal";
import StatCard from "./ui/StatCard";
import EmptyState from "./ui/EmptyState";
import LoadingSpinner from "./ui/LoadingSpinner";
import SalarySlipPrint from "./SalarySlipPrint";

export default function Salaries({ reviewers, month, toast, user }) {
  const isReadOnly = user?.role === "auditor";
  const [salaries, setSalaries] = useState([]);
  const [modal, setModal] = useState(null);
  const [isNewEntry, setIsNewEntry] = useState(true);
  const [slip, setSlip] = useState(null);
  const [slipLoading, setSlipLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [form, setForm] = useState({
    reviewer_id: "",
    month,
    base_salary: 300,
    deduction: 0,
    bonus: 0,
    notes: "",
  });

  const load = useCallback(async () => {
    try {
      const r = await api(`/salaries?month=${month}`);
      setSalaries(r || []);
    } catch {}
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setForm(f => ({ ...f, month }));
  }, [month]);

  // Auto-fill the reviewer's performance data (stations, replies, accuracy,
  // send-time rates, base salary) as soon as a reviewer + month are picked
  // on a NEW salary entry — the supervisor only has to type deduction/bonus.
  // Skipped when editing an existing salary record so we don't overwrite
  // its already-saved figures with this month's live numbers.
  useEffect(() => {
    if (!isNewEntry || !form.reviewer_id || !form.month) {
      setSlip(null);
      return;
    }
    let cancelled = false;
    setSlipLoading(true);
    api(`/reports/salary-slip?reviewer_id=${form.reviewer_id}&month=${form.month}`)
      .then((r) => {
        if (cancelled) return;
        setSlip(r);
        setForm((f) => ({ ...f, base_salary: r.base_salary }));
      })
      .catch(() => { if (!cancelled) setSlip(null); })
      .finally(() => { if (!cancelled) setSlipLoading(false); });
    return () => { cancelled = true; };
  }, [isNewEntry, form.reviewer_id, form.month]);

  const save = async () => {
    if (!form.reviewer_id) {
      toast("اختر المراجع", "warning");
      return;
    }
    try {
      await api("/salaries", {
        method: "POST",
        body: {
          reviewer_id: parseInt(form.reviewer_id),
          month: form.month,
          base_salary: parseFloat(form.base_salary),
          deduction: parseFloat(form.deduction || 0),
          bonus: parseFloat(form.bonus || 0),
          notes: form.notes,
        },
      });
      toast("✅ تم الحفظ");
      setModal(null);
      // Show the printable A4 slip right after saving, with whatever
      // base/deduction/bonus values are in the form now. Re-fetch the
      // performance data if this was an edit (slip wasn't auto-loaded then)
      // so printing works the same way for both new and edited entries.
      let slipForPrint = slip;
      if (!slipForPrint) {
        try {
          slipForPrint = await api(`/reports/salary-slip?reviewer_id=${form.reviewer_id}&month=${form.month}`);
        } catch {
          slipForPrint = null;
        }
      }
      if (slipForPrint) setPreviewData({ slip: slipForPrint, form: { ...form } });
      load();
    } catch (e) {
      toast("خطأ: " + e.message, "error");
    }
  };

  const total = salaries.reduce((s, r) => s + (r.net_salary || 0), 0);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <h2
          style={{
            fontSize: 20,
            fontWeight: 800,
            margin: 0,
            color: "#f1f5f9",
          }}
        >
          💰 رواتب {month}
        </h2>
        {!isReadOnly && (
          <Btn
            onClick={() => {
              setForm({
                reviewer_id: "",
                month,
                base_salary: 300,
                deduction: 0,
                bonus: 0,
                notes: "",
              });
              setIsNewEntry(true);
              setSlip(null);
              setModal("add");
            }}
            color="#22c55e"
          >
            + إضافة
          </Btn>
        )}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <StatCard
          icon="💰"
          label="إجمالي الرواتب"
          value={`${comma(total)} ر.س`}
          color="#22c55e"
        />
        <StatCard
          icon="👥"
          label="عدد المراجعين"
          value={salaries.length}
          color="#38bdf8"
        />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))",
          gap: 12,
        }}
      >
        {salaries.map((s) => (
          <Card key={s.id} style={{ padding: "16px 18px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div
                style={{ color: "#f1f5f9", fontWeight: 700, fontSize: 13 }}
              >
                {s.reviewer_name}
              </div>
              <span
                style={{
                  color: "#22c55e",
                  fontWeight: 800,
                  fontSize: 16,
                }}
              >
                {comma(s.net_salary)} ر.س
              </span>
            </div>
            {[
              ["الأساسي", s.base_salary, "#38bdf8"],
              ["الخصومات", s.deduction, "#ef4444"],
              ["الحوافز", s.bonus, "#22c55e"],
            ].map(([l, v, c]) => (
              <div
                key={l}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "3px 0",
                }}
              >
                <span style={{ color: "#64748b", fontSize: 12 }}>{l}</span>
                <span
                  style={{ color: c, fontSize: 12, fontWeight: 600 }}
                >
                  {comma(v)} ر.س
                </span>
              </div>
            ))}
            {!isReadOnly && (
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Btn
                  size="sm"
                  color="#38bdf8"
                  onClick={() => {
                    setForm({
                      reviewer_id: s.reviewer_id,
                      month: s.month,
                      base_salary: s.base_salary,
                      deduction: s.deduction,
                      bonus: s.bonus,
                      notes: s.notes || "",
                    });
                    setIsNewEntry(false);
                    setSlip(null);
                    setModal("add");
                  }}
                >
                  تعديل
                </Btn>
              </div>
            )}
          </Card>
        ))}
        {salaries.length === 0 && (
          <div style={{ gridColumn: "1/-1" }}>
            <EmptyState message="لا توجد بيانات" />
          </div>
        )}
      </div>
      {modal && (
        <Modal title="راتب المراجع" onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Select
              label="المراجع"
              value={form.reviewer_id}
              onChange={(v) => setForm((f) => ({ ...f, reviewer_id: v }))}
              options={reviewers.map((r) => ({
                value: r.id,
                label: r.name,
              }))}
              required
            />
            <Input
              label="الشهر"
              type="month"
              value={form.month}
              onChange={(v) => setForm((f) => ({ ...f, month: v }))}
            />

            {isNewEntry && slipLoading && <LoadingSpinner />}

            {isNewEntry && !slipLoading && slip && (
              <div
                style={{
                  background: "#0f172a", border: "1px solid #1e293b",
                  borderRadius: 10, padding: "12px 14px",
                  display: "flex", flexDirection: "column", gap: 6,
                }}
              >
                <div style={{ color: "#38bdf8", fontWeight: 700, fontSize: 12, marginBottom: 2 }}>
                  📋 بيانات {slip.reviewer_name} تلقائيًا لهذا الشهر
                </div>
                {[
                  ["عدد المواقع", slip.station_count],
                  ["إجمالي التشغيلي", comma(slip.total_op)],
                  ["إجمالي الكاميرات", comma(slip.total_cam)],
                  ["الفرق الصافي", comma(slip.total_diff)],
                  ["أيام لم يتم عملها", `${slip.days_not_worked} من ${slip.days_in_month}`],
                  ["نسبة الدقة", slip.accuracy != null ? pct(slip.accuracy) : "—"],
                  ["تقييم الأداء", slip.accuracy != null ? accuracyLabel(slip.accuracy) : "—"],
                  ["إرسال قبل 9ص", slip.before_pct != null ? pct(slip.before_pct) : "—"],
                  ["إرسال بعد 9ص", slip.after_pct != null ? pct(slip.after_pct) : "—"],
                  ["لم يُرسل خلال اليوم", slip.not_sent_pct != null ? pct(slip.not_sent_pct) : "—"],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                    <span style={{ color: "#64748b" }}>{l}</span>
                    <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
                {slip.diff_reasons?.length > 0 && (
                  <div style={{ marginTop: 6, paddingTop: 8, borderTop: "1px solid #1e293b" }}>
                    <div style={{ color: "#f59e0b", fontWeight: 700, fontSize: 11, marginBottom: 4 }}>
                      ❗ أسباب الفروقات هذا الشهر
                    </div>
                    {slip.diff_reasons.map((r) => (
                      <div key={r.reason} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                        <span style={{ color: "#94a3b8" }}>{r.reason}</span>
                        <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{r.count} يوم</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 10,
              }}
            >
              <Input
                label="الأساسي"
                type="number"
                value={form.base_salary}
                onChange={(v) => setForm((f) => ({ ...f, base_salary: v }))}
              />
              <Input
                label="الخصومات"
                type="number"
                value={form.deduction}
                onChange={(v) => setForm((f) => ({ ...f, deduction: v }))}
              />
              <Input
                label="الحوافز"
                type="number"
                value={form.bonus}
                onChange={(v) => setForm((f) => ({ ...f, bonus: v }))}
              />
            </div>
            {(form.base_salary || form.deduction || form.bonus) && (
              <div
                style={{
                  background: "#0f172a",
                  borderRadius: 8,
                  padding: "9px 12px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ color: "#94a3b8" }}>صافي المستحق:</span>
                <span
                  style={{
                    color: "#22c55e",
                    fontWeight: 800,
                    fontSize: 16,
                  }}
                >
                  {comma(
                    (parseFloat(form.base_salary) || 0) -
                      (parseFloat(form.deduction) || 0) +
                      (parseFloat(form.bonus) || 0)
                  )}{" "}
                  ر.س
                </span>
              </div>
            )}
            <Input
              label="ملاحظات"
              value={form.notes}
              onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <Btn onClick={save} color="#22c55e" size="lg">
                💾 حفظ
              </Btn>
              <Btn onClick={() => setModal(null)} color="#64748b" size="lg">
                إلغاء
              </Btn>
            </div>
          </div>
        </Modal>
      )}
      {previewData && (
        <SalarySlipPrint
          slip={previewData.slip}
          form={previewData.form}
          onClose={() => setPreviewData(null)}
        />
      )}
    </div>
  );
}
